import { Button } from '@material-ui/core';
import { Link } from 'gatsby';
import { ArrowRight, Briefcase, Check, User, Users } from 'lucide-react';
import React, { useState } from 'react';
import styled from '../styled';
import { COLORS } from '../themes/color';
import { DemoButton } from './DemoButton';
import { EarlyAccessDialog } from './EarlyAccessDialog';
import { Reveal } from './motion';
import { ProductIcon } from './ProductIcon';

const PlanGrid = styled('div')`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: left;

  > * {
    height: 100%;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    max-width: 560px;

    /* Lead with the plans people can buy today */
    > *:first-child {
      order: 2;
    }
  }
`;

const PlanCard = styled('div')<{ featured?: boolean }>`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background-color: #fff;
  border: 1px solid
    ${(p) => (p.featured ? 'rgba(46, 92, 255, 0.35)' : '#e2e8f0')};
  border-radius: 16px;
  padding: 32px 28px;
  box-shadow: ${(p) =>
    p.featured
      ? '0 12px 40px rgba(46, 92, 255, 0.1)'
      : '0 4px 20px rgba(14, 28, 43, 0.04)'};
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const PlanTitle = styled('h3')`
  font-size: 20px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const PlanDescription = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const PlanList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const PlanItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const CtaRow = styled('div')`
  margin-top: auto;
  padding-top: 14px;
`;

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  text-decoration: none;

  svg {
    transition: transform 0.2s;
  }

  &:hover svg {
    transform: translateX(2px);
  }
`;

export const PricingTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <PlanGrid>
        <Reveal style={{ height: '100%' }}>
          <PlanCard>
            <ProductIcon $size="lg" $muted>
              <User size={20} />
            </ProductIcon>
            <PlanTitle>Creators</PlanTitle>
            <PlanDescription>
              Track all your affiliate revenue in one place, with features and
              tools tailored for creator-led businesses.
            </PlanDescription>
            <PlanList>
              <PlanItem>
                <Check size={16} />
                All your affiliate commissions in one dashboard
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                Centralized link creation and management
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                See which content and links earn the most across your social
                channels
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                Set up in minutes without a developer
              </PlanItem>
            </PlanList>
            <CtaRow>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                style={{
                  height: '48px',
                  borderWidth: '2px',
                  fontWeight: 700
                }}
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                Request early access
              </Button>
            </CtaRow>
          </PlanCard>
        </Reveal>
        <Reveal delay={120} style={{ height: '100%' }}>
          <PlanCard featured>
            <ProductIcon $size="lg">
              <Users size={20} />
            </ProductIcon>
            <PlanTitle>Premium publishers</PlanTitle>
            <PlanDescription>
              Commerce content insights, affiliate reporting, and campaign
              infrastructure for media companies and enterprise publishers.
            </PlanDescription>
            <PlanList>
              <PlanItem>
                <Check size={16} />
                <span>
                  <strong>Unify</strong>: Transactions, rates, and payouts from
                  100+ networks
                </span>
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                <span>
                  <strong>Optimize</strong>: Page, link, and placement-level
                  performance, onsite and offsite
                </span>
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                <span>
                  <strong>Amplify</strong>: Sponsorship campaign infrastructure
                  with AI-powered yield optimization
                </span>
              </PlanItem>
            </PlanList>
            <CtaRow>
              <DemoButton text="Schedule a demo" variant="contained" />
            </CtaRow>
          </PlanCard>
        </Reveal>
        <Reveal delay={240} style={{ height: '100%' }}>
          <PlanCard>
            <ProductIcon $size="lg">
              <Briefcase size={20} />
            </ProductIcon>
            <PlanTitle>Merchants &amp; Agencies</PlanTitle>
            <PlanDescription>
              Build and maintain your edge in brand, PR, and AI-driven discovery
              with intelligent monitoring and campaign execution tools.
            </PlanDescription>
            <PlanList>
              <PlanItem>
                <Check size={16} />
                Match with publishers already promoting your products through
                other sellers
              </PlanItem>
              <PlanItem>
                <Check size={16} />
                Deploy sponsorship campaigns with good-fit publishers for new
                content or link swaps
              </PlanItem>
            </PlanList>
            <CtaRow>
              <SecondaryLink to="/demand/">
                Learn more about demand solutions
                <ArrowRight size={14} />
              </SecondaryLink>
              <br />
              <br />
              <DemoButton text="Schedule a demo" variant="contained" />
            </CtaRow>
          </PlanCard>
        </Reveal>
      </PlanGrid>
      <EarlyAccessDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
    </>
  );
};
